// Turning outlines into triangles: ear-clipping for fills, and offsetting for
// strokes with the joins, caps and dash patterns the map uses.
//
// Everything here works in a shape's own coordinates and is cached by the caller,
// so it runs when a shape's geometry changes rather than per frame.

// ---------------------------------------------------------------------------
// Fills
// ---------------------------------------------------------------------------

// Ear clipping over a simple polygon given as a flat [x, y, ...] array. Returns
// index triples. Every filled shape the map draws is a simple polygon — rounded
// rectangles, annular pie segments, wedges and callout tails — so there is no
// hole handling here; a shape that needed it would come out as its outer ring.
export function triangulate(points) {
  const n = points.length >> 1;
  if (n < 3) return [];

  const indices = new Array(n);
  for (let i = 0; i < n; i++) indices[i] = i;

  // Ear clipping needs a known winding; normalise to counter-clockwise.
  if (signedArea(points, indices) < 0) indices.reverse();

  const out = [];
  let remaining = n;
  let guard = remaining * remaining;

  let i = 0;
  while (remaining > 3 && guard-- > 0) {
    const prev = indices[(i + remaining - 1) % remaining];
    const curr = indices[i % remaining];
    const next = indices[(i + 1) % remaining];

    if (isEar(points, indices, remaining, prev, curr, next)) {
      out.push(prev, curr, next);
      indices.splice(i % remaining, 1);
      remaining--;
      if (i > 0) i--;
    } else {
      i++;
    }
  }

  if (remaining === 3) out.push(indices[0], indices[1], indices[2]);

  return out;
}

function signedArea(points, indices) {
  let area = 0;
  for (let i = 0; i < indices.length; i++) {
    const a = indices[i] * 2;
    const b = indices[(i + 1) % indices.length] * 2;
    area += points[a] * points[b + 1] - points[b] * points[a + 1];
  }
  return area / 2;
}

function isEar(points, indices, count, prev, curr, next) {
  const ax = points[prev * 2];
  const ay = points[prev * 2 + 1];
  const bx = points[curr * 2];
  const by = points[curr * 2 + 1];
  const cx = points[next * 2];
  const cy = points[next * 2 + 1];

  // Reflex vertices cannot be ears (winding is counter-clockwise here).
  const cross = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
  if (cross <= 0) return false;

  for (let i = 0; i < count; i++) {
    const idx = indices[i];
    if (idx === prev || idx === curr || idx === next) continue;
    if (
      pointInTriangle(
        points[idx * 2],
        points[idx * 2 + 1],
        ax,
        ay,
        bx,
        by,
        cx,
        cy
      )
    )
      return false;
  }
  return true;
}

function pointInTriangle(px, py, ax, ay, bx, by, cx, cy) {
  const d1 = (px - bx) * (ay - by) - (ax - bx) * (py - by);
  const d2 = (px - cx) * (by - cy) - (bx - cx) * (py - cy);
  const d3 = (px - ax) * (cy - ay) - (cx - ax) * (py - ay);
  const hasNeg = d1 < 0 || d2 < 0 || d3 < 0;
  const hasPos = d1 > 0 || d2 > 0 || d3 > 0;
  return !(hasNeg && hasPos);
}

// ---------------------------------------------------------------------------
// Strokes
// ---------------------------------------------------------------------------

const MITER_LIMIT = 4;
const ROUND_SEGMENTS = 8;

// Expands a polyline into triangles, appending [x0, y0, x1, y1, x2, y2, ...] to
// `out`. `cap` is "butt", "round" or "square"; joins follow SVG's miter default
// with the standard limit, falling back to a bevel beyond it.
export function strokePolyline(points, closed, width, cap, out) {
  const half = width / 2;
  if (half <= 0) return;

  const pts = dedupe(points, closed);
  const n = pts.length >> 1;

  if (n < 2) {
    // A degenerate subpath still paints a dot under a round cap, as in SVG.
    if (n === 1 && cap === "round")
      pushDisc(out, pts[0], pts[1], half, ROUND_SEGMENTS * 2);
    return;
  }

  const segmentCount = closed ? n : n - 1;

  for (let i = 0; i < segmentCount; i++) {
    const ax = pts[i * 2];
    const ay = pts[i * 2 + 1];
    const j = (i + 1) % n;
    const bx = pts[j * 2];
    const by = pts[j * 2 + 1];

    const dx = bx - ax;
    const dy = by - ay;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) continue;

    const nx = (-dy / len) * half;
    const ny = (dx / len) * half;

    pushQuad(
      out,
      ax + nx,
      ay + ny,
      bx + nx,
      by + ny,
      bx - nx,
      by - ny,
      ax - nx,
      ay - ny
    );
  }

  // Joins fill the wedge left between consecutive segments.
  const jointCount = closed ? n : n - 2;
  for (let k = 0; k < jointCount; k++) {
    const i = closed ? k : k + 1;
    const p = (i - 1 + n) % n;
    const q = (i + 1) % n;
    pushJoin(
      out,
      pts[p * 2],
      pts[p * 2 + 1],
      pts[i * 2],
      pts[i * 2 + 1],
      pts[q * 2],
      pts[q * 2 + 1],
      half
    );
  }

  if (!closed) {
    pushCap(out, pts[2], pts[3], pts[0], pts[1], half, cap);
    pushCap(
      out,
      pts[(n - 2) * 2],
      pts[(n - 2) * 2 + 1],
      pts[(n - 1) * 2],
      pts[(n - 1) * 2 + 1],
      half,
      cap
    );
  }
}

function dedupe(points, closed) {
  const out = [];
  for (let i = 0; i < points.length; i += 2) {
    const n = out.length;
    if (n >= 2) {
      const dx = points[i] - out[n - 2];
      const dy = points[i + 1] - out[n - 1];
      if (dx * dx + dy * dy < 1e-18) continue;
    }
    out.push(points[i], points[i + 1]);
  }
  // A closed subpath often repeats its first point; the wrap-around handles it.
  if (closed && out.length >= 4) {
    const dx = out[0] - out[out.length - 2];
    const dy = out[1] - out[out.length - 1];
    if (dx * dx + dy * dy < 1e-18) out.length -= 2;
  }
  return out;
}

function pushJoin(out, ax, ay, bx, by, cx, cy, half) {
  const d1x = bx - ax;
  const d1y = by - ay;
  const d2x = cx - bx;
  const d2y = cy - by;
  const l1 = Math.hypot(d1x, d1y);
  const l2 = Math.hypot(d2x, d2y);
  if (l1 < 1e-9 || l2 < 1e-9) return;

  const u1x = d1x / l1;
  const u1y = d1y / l1;
  const u2x = d2x / l2;
  const u2y = d2y / l2;

  const cross = u1x * u2y - u1y * u2x;
  if (Math.abs(cross) < 1e-9) return; // collinear: nothing to fill

  // Outer side of the turn.
  const sign = cross > 0 ? -1 : 1;
  const p1x = bx + -u1y * half * sign;
  const p1y = by + u1x * half * sign;
  const p2x = bx + -u2y * half * sign;
  const p2y = by + u2x * half * sign;

  // Miter point, used when it stays within the limit; otherwise a bevel.
  const dot = u1x * u2x + u1y * u2y;
  const cosHalf = Math.sqrt(Math.max(0, (1 + dot) / 2));
  if (cosHalf > 1e-6 && 1 / cosHalf <= MITER_LIMIT) {
    let mx = -(u1y + u2y);
    let my = u1x + u2x;
    const ml = Math.hypot(mx, my);
    if (ml > 1e-9) {
      const scale = (half / cosHalf) * sign;
      mx = bx + (mx / ml) * scale;
      my = by + (my / ml) * scale;
      pushTriangle(out, bx, by, p1x, p1y, mx, my);
      pushTriangle(out, bx, by, mx, my, p2x, p2y);
      return;
    }
  }

  pushTriangle(out, bx, by, p1x, p1y, p2x, p2y);
}

function pushCap(out, fromX, fromY, atX, atY, half, cap) {
  if (cap !== "round" && cap !== "square") return;

  const dx = atX - fromX;
  const dy = atY - fromY;
  const len = Math.hypot(dx, dy);
  if (len < 1e-9) return;

  const ux = dx / len;
  const uy = dy / len;
  const nx = -uy * half;
  const ny = ux * half;

  if (cap === "square") {
    const ex = atX + ux * half;
    const ey = atY + uy * half;
    pushQuad(
      out,
      atX + nx,
      atY + ny,
      ex + nx,
      ey + ny,
      ex - nx,
      ey - ny,
      atX - nx,
      atY - ny
    );
    return;
  }

  // Round: a fan from the end point over the outward half-circle.
  const startAngle = Math.atan2(ny, nx);
  for (let i = 0; i < ROUND_SEGMENTS; i++) {
    const a0 = startAngle - (Math.PI * i) / ROUND_SEGMENTS;
    const a1 = startAngle - (Math.PI * (i + 1)) / ROUND_SEGMENTS;
    pushTriangle(
      out,
      atX,
      atY,
      atX + Math.cos(a0) * half,
      atY + Math.sin(a0) * half,
      atX + Math.cos(a1) * half,
      atY + Math.sin(a1) * half
    );
  }
}

function pushDisc(out, cx, cy, radius, segments) {
  for (let i = 0; i < segments; i++) {
    const a0 = (Math.PI * 2 * i) / segments;
    const a1 = (Math.PI * 2 * (i + 1)) / segments;
    pushTriangle(
      out,
      cx,
      cy,
      cx + Math.cos(a0) * radius,
      cy + Math.sin(a0) * radius,
      cx + Math.cos(a1) * radius,
      cy + Math.sin(a1) * radius
    );
  }
}

function pushTriangle(out, ax, ay, bx, by, cx, cy) {
  out.push(ax, ay, bx, by, cx, cy);
}

function pushQuad(out, ax, ay, bx, by, cx, cy, dx, dy) {
  out.push(ax, ay, bx, by, cx, cy, ax, ay, cx, cy, dx, dy);
}

// ---------------------------------------------------------------------------
// Dashes
// ---------------------------------------------------------------------------

// Splits a polyline into the "on" runs of a dash pattern. Returns an array of
// flat point arrays, each of which is stroked as its own open subpath.
export function applyDashes(points, closed, pattern) {
  const pts = closed ? points.concat(points[0], points[1]) : points;
  const period = pattern.reduce((a, b) => a + b, 0);
  if (period <= 0) return [pts];

  const runs = [];
  let current = null;

  let patternIndex = 0;
  let remaining = pattern[0];
  let on = true;

  for (let i = 0; i + 3 < pts.length; i += 2) {
    let ax = pts[i];
    let ay = pts[i + 1];
    const bx = pts[i + 2];
    const by = pts[i + 3];

    let segLength = Math.hypot(bx - ax, by - ay);
    if (segLength < 1e-9) continue;

    let ux = (bx - ax) / segLength;
    let uy = (by - ay) / segLength;

    if (on && !current) current = [ax, ay];

    while (segLength > remaining) {
      const nx = ax + ux * remaining;
      const ny = ay + uy * remaining;

      if (on) {
        current.push(nx, ny);
        runs.push(current);
        current = null;
      } else {
        current = [nx, ny];
      }

      ax = nx;
      ay = ny;
      segLength -= remaining;

      patternIndex = (patternIndex + 1) % pattern.length;
      remaining = pattern[patternIndex];
      on = !on;

      // A zero-length entry would otherwise spin here forever.
      if (remaining <= 0) remaining = 1e-6;
    }

    remaining -= segLength;
    if (on && current) current.push(bx, by);
  }

  if (current && current.length >= 4) runs.push(current);

  return runs;
}
